import { isNotEmpty, noop } from "neetocist";
import PropTypes from "prop-types";
import { isEmpty, isNotNil } from "ramda";

import { MAX_FILE_SIZE } from "./constants";
import DropZone from "./DropZone";
import ErrorMessages from "./ErrorMessages";
import "./file-upload.scss";
import FilePreview from "./FilePreview";
import useFileUpload from "./hooks/useFileUpload";
import { getAcceptedFileTypes } from "./utils";

const FileUpload = ({
  allowedFileTypes = "",
  multipleFilesAllowed = true,
  maxFileSize = MAX_FILE_SIZE,
  hasFileSizeLimit = true,
  isPreview = false,
  minFileSize = 0,
  files = [],
  onChange = noop,
  error,
  setError = noop,
  setTouched = noop,
}) => {
  const {
    progress,
    cancel,
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
  } = useFileUpload({
    disabled: isPreview,
    accept: getAcceptedFileTypes(allowedFileTypes),
    multiple: multipleFilesAllowed,
    maxFileSize,
    minFileSize,
    files,
    onChange,
    setError,
    setTouched,
    hasFileSizeLimit,
  });

  const maxFileSizeInBytes = maxFileSize * 1000000;

  const isUploadingFile = file =>
    isNotNil(progress[file.name]) && (progress[file.name] !== 100 || !file.url);

  const shouldShowError = error && isEmpty(fileRejections);

  return (
    <>
      <DropZone
        {...{
          allowedFileTypes,
          getInputProps,
          getRootProps,
          hasFileSizeLimit,
          isDragActive,
          maxFileSize,
          multipleFilesAllowed,
        }}
      />
      {!isEmpty(files) && (
        <ul className="neeto-molecules-file-upload__uploaded-list">
          {files?.map((file, idx) => (
            <FilePreview
              {...{ file }}
              cancel={cancel(idx)}
              isUploading={isUploadingFile(file)}
              key={idx}
              progress={progress[file.name]}
            />
          ))}
        </ul>
      )}
      {isNotEmpty(fileRejections) && (
        <ErrorMessages {...{ fileRejections, maxFileSizeInBytes }} />
      )}
      {shouldShowError && (
        <p
          className="neeto-molecules-input__error-text"
          data-testid="form-file-upload-input-error"
        >
          <span className="neeto-molecules-input__error-text-style-handle">
            {error}
          </span>
        </p>
      )}
    </>
  );
};

FileUpload.propTypes = {
  /**
   * Allowed file types for the file upload component.
   */
  allowedFileTypes: PropTypes.string,
  /**
   * Boolean to allow multiple files to be uploaded.
   */
  multipleFilesAllowed: PropTypes.bool,
  /**
   * Maximum file size allowed for the file upload component(MB).
   */
  maxFileSize: PropTypes.number,
  /**
   * Boolean to check if file size limit is enabled.
   */
  hasFileSizeLimit: PropTypes.bool,
  /**
   * Boolean to use component as a preview component.
   */
  isPreview: PropTypes.bool,
  /**
   * Minimum file size allowed for the file upload component(MB).
   */
  minFileSize: PropTypes.number,
  /**
   * Uploaded files.
   */
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      size: PropTypes.number,
      url: PropTypes.string,
    })
  ),
  /**
   * Function to be called on file upload, with the updated files array.
   */
  onChange: PropTypes.func,
  /**
   * Error message to be displayed.
   */
  error: PropTypes.string,
  /**
   * Function to set error message.
   */
  setError: PropTypes.func,
};

export default FileUpload;
